import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

const STORAGE_KEY = "pumaria.consent.v1";

const updateGtag = (prefs) => {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("consent", "update", {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
};

const CookieBanner = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  // Derive current language from the URL, not from a route param
  // (CookieBanner lives outside the /:lang route, so useParams() is empty).
  const firstSegment = location.pathname.split("/").filter(Boolean)[0];
  const cur = firstSegment === "en" ? "en" : "pe";
  const isThankYou = location.pathname === "/thank_you";

  useEffect(() => {
    if (isThankYou) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(raw);
        updateGtag(parsed);
      }
    } catch {
      setVisible(true);
    }
  }, [isThankYou]);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* ignore */
    }
    updateGtag(value);
    setVisible(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!visible || isThankYou) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label={t("cookies.banner.title")}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        background: "#fff",
        borderTop: "3px solid var(--pum-navy)",
        boxShadow: "0 -8px 24px rgba(11,31,58,0.12)",
      }}
    >
      <div
        className="pum-container"
        style={{
          padding: "1.5rem 1.25rem",
          display: "flex",
          gap: "1.75rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 420px", minWidth: "260px" }}>
          <h3 style={{ fontSize: "1.05rem", marginBottom: "0.4rem" }}>{t("cookies.banner.title")}</h3>
          <p style={{ fontSize: "0.88rem", color: "var(--pum-gray-700)", lineHeight: 1.55 }}>
            {t("cookies.banner.text")}{" "}
            <Link
              to={`/${cur}/cookies`}
              data-testid="cookie-banner-policy-link"
              style={{ color: "var(--pum-navy)", textDecoration: "underline", fontWeight: 600 }}
            >
              {t("cookies.banner.policy")}
            </Link>
          </p>

          {showPrefs && (
            <div
              data-testid="cookie-prefs"
              style={{
                marginTop: "1rem",
                padding: "1rem",
                background: "var(--pum-mist)",
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div>
                <label style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", fontSize: "0.9rem" }}>
                  <span><strong>{t("cookies.prefs.necessary")}</strong><br />
                    <span style={{ fontSize: "0.8rem", color: "var(--pum-gray-500)" }}>{t("cookies.prefs.necessaryText")}</span>
                  </span>
                  <input type="checkbox" checked disabled data-testid="cookie-pref-necessary" />
                </label>
              </div>
              <div>
                <label style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", fontSize: "0.9rem" }}>
                  <span><strong>{t("cookies.prefs.analytics")}</strong><br />
                    <span style={{ fontSize: "0.8rem", color: "var(--pum-gray-500)" }}>{t("cookies.prefs.analyticsText")}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    data-testid="cookie-pref-analytics"
                  />
                </label>
              </div>
              <div>
                <label style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", fontSize: "0.9rem" }}>
                  <span><strong>{t("cookies.prefs.marketing")}</strong><br />
                    <span style={{ fontSize: "0.8rem", color: "var(--pum-gray-500)" }}>{t("cookies.prefs.marketingText")}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    data-testid="cookie-pref-marketing"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", minWidth: "180px" }}>
          <button
            className="pum-btn pum-btn--primary"
            onClick={acceptAll}
            data-testid="cookie-accept-all"
          >
            {t("cookies.banner.accept")}
          </button>
          {showPrefs ? (
            <button className="pum-btn pum-btn--emerald" onClick={savePrefs} data-testid="cookie-save-prefs">
              {t("cookies.prefs.save")}
            </button>
          ) : (
            <button
              className="pum-btn pum-btn--ghost"
              onClick={() => setShowPrefs(true)}
              data-testid="cookie-open-prefs"
            >
              {t("cookies.banner.settings")}
            </button>
          )}
          <button
            className="pum-btn pum-btn--ghost"
            onClick={rejectAll}
            data-testid="cookie-reject-all"
          >
            {t("cookies.banner.reject")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
